import { TreeGridRows, WidgetTriggerPathType, WidgetUUID } from '@/common/utils/types';
import { type HeatmapChartOption } from '@/dashboard/features/widgets/heatmap/domain/heatmapSetting.types';
import { type HeatmapRawData } from '@/dashboard/features/widgets/heatmap/domain/heatmapWidget.types';
import {
  CalendarTimeRange,
  DisplayStyleType,
  FilterWidgetChartType,
  WidgetChartData,
  WidgetChartType,
  WidgetColorOption,
  WidgetCreateType,
  WidgetExtraData,
  WidgetModeType,
  WidgetTimePeriod,
  WidgetTitleOption,
  WidgetType,
  WidgetPastedInfo,
} from '@/dashboard/shared/types/widgets.types';
import { CurrentRawData } from '@/worker/commands/dashboard/current';
import { MetricRawData } from '@/worker/commands/dashboard/metrics';
import { WidgetScatterRawData } from '@/worker/commands/dashboard/scatter';
import { RowInfo, TableRawData } from '@/worker/commands/dashboard/table';
import { AlertMetricRawData } from '@/worker/commands/dashboard/alertMetric';
import { TopologyRawData } from '@/worker/commands/dashboard/topology/topology.types';
import { DataResource } from '@/dashboard/features/editToolbox/types/dataResource.types';
import { ClusterWidgetChartData } from '@/dashboard/features/widgets/cluster/settings/domain/clusterData';
import { OneDayCompareChartOption } from '@/dashboard/features/widgets/oneDayCompare/settings/types/oneDayCompareSettings.types';
import { FilterTableData } from '@/dashboard/features/widgets/filter/settings/components/filterDataList.setup';
import { EqualizerChartOption } from '@/dashboard/features/widgets/equalizer/settings/components/equalizerSettingsArticle.setup';
import { GaugeChartOption } from '@/dashboard/features/widgets/gauge/settings/components/gaugeSettingArticle.setup';
import { TimeSeriesChartOption } from '@/dashboard/features/widgetSettings/editModeWidgetSettings/components/graphs/types/graphWidgetSettings.types';
import { ScatterChartOption } from '@/dashboard/features/widgets/scatter/settings/scatterSettingsArticle.setup';
import { StackedProportionBarChartOption } from '../../stackedProportionBar/settings/components/stackedProportionBarSettingsArticle.setup';
import { ScoreboardChartOption } from '@/dashboard/features/widgets/scoreboard/settings/types/scoreboardWidgetOption.types';
import { PieChartOption } from '@/dashboard/features/widgets/pie/settings/domain/pieWidgetChartOption';
import { TopologyWidgetChartOption } from '@/dashboard/features/widgetSettings/editModeWidgetSettings/components/architecture/types/architectureWidgetSettings.types';
import type { AlertChartOption } from '@/dashboard/features/widgets/alert/settings/domain/alertChartOption';
import { ClusterRawData } from '../../cluster/widget/domain/types';

export type ChartOptions<T extends WidgetChartType> = T extends 'TIME_SERIES'
  ? TimeSeriesChartOption
  : T extends 'ONE_DAY_COMPARE'
    ? OneDayCompareChartOption
    : T extends 'GAUGE'
      ? GaugeChartOption
      : T extends 'PIE'
        ? PieChartOption
        : T extends FilterWidgetChartType
          ? undefined
          : T extends 'SCATTER'
            ? ScatterChartOption
            : T extends 'STACKED_PROPORTION_BAR'
              ? StackedProportionBarChartOption
              : T extends 'EQUALIZER'
                ? EqualizerChartOption
                : T extends 'SCOREBOARD'
                  ? ScoreboardChartOption
                  : T extends 'HEATMAP'
                    ? HeatmapChartOption
                    : T extends 'TOPOLOGY'
                      ? TopologyWidgetChartOption
                      : T extends 'REALTIME' | 'BY_PERIOD'
                        ? AlertChartOption
                        : any;

export type FilterOptions<T extends WidgetChartType> = T extends
  | 'HORIZONTAL_FILTER'
  | 'VERTICAL_FILTER'
  ? { filterList: FilterTableData[] }
  : T extends 'PIE'
    ? undefined
    : undefined;

export interface WidgetProps<T extends WidgetChartType = any> {
  widgetId: WidgetUUID;
  widgetType: WidgetType;
  createType: WidgetCreateType;
  chartType: T;
  titleOption: WidgetTitleOption;
  displayStyle: DisplayStyleType;
  noDataMsg: string;
  dataResource: DataResource;
  widgetColor?: WidgetColorOption;
  timePeriod?: WidgetTimePeriod;
  colorTheme?: string;
  chartData?: WidgetChartData[];
  mode?: WidgetModeType;
  chartOption: ChartOptions<T>;
  filterOption?: FilterOptions<T>;
  extraData?: WidgetExtraData[];
  calendarTimeRange?: CalendarTimeRange[];
  isInViewport?: boolean;
  isPopup?: boolean; // 팝업에서 사용
  pastedInfo?: WidgetPastedInfo;
}

export interface WidgetEmit {
  (e: 'update:chartOption', chartOption: any): void;
}

export interface ActionViewItem {
  begin: number;
  active: number;
  normal: number;
  warning: number;
  critical: number;
  end: number;
}

export interface SelectedTimeSeriesData {
  seriesId: string[];
}

export type ChartRawData =
  | MetricRawData
  | CurrentRawData
  | TableRawData
  | TopologyRawData
  | HeatmapRawData
  | ClusterRawData
  | WidgetScatterRawData
  | AlertMetricRawData;

export const ChartDataType = [
  'metrics',
  'current',
  'table',
  'scatter',
  'topology',
  'heatmap',
  'cluster',
] as const;
export type ChartDataType = (typeof ChartDataType)[number];

export const MetricChartType = ['TIME_SERIES', 'ONE_DAY_COMPARE'] as const;
export type MetricChartType = Extract<WidgetChartType, (typeof MetricChartType)[number]>;

export const CurrentChartType = [
  'EXCLUSIVE',
  'SCOREBOARD',
  'STACKED_PROPORTION_BAR',
  'GAUGE',
] as const;
export type CurrentChartType = Extract<WidgetChartType, (typeof CurrentChartType)[number]>;

export const TableChartType = [
  'EQUALIZER',
  'ACTION_VIEW',
  'TABLE',
  'STATUS_SUMMARY',
  'PIE',
  'SERVICE_CARD',
  'TOP_LIST',
  'STATUS',
  'TREE_GRID',
  'SERVER',
] as const;
export type TableChartType = Extract<WidgetChartType, (typeof TableChartType)[number]>;

export const ScatterChartType = ['SCATTER'] as const;
export type ScatterChartType = Extract<WidgetChartType, (typeof ScatterChartType)[number]>;

export const TopologyChartType = ['TOPOLOGY'] as const;
export type TopologyChartType = Extract<WidgetChartType, (typeof TopologyChartType)[number]>;

export const HeatmapChartType = ['HEATMAP'] as const;
export type HeatmapChartType = Extract<WidgetChartType, (typeof HeatmapChartType)[number]>;

export const ClusterChartType = ['CLUSTER'] as const;
export type ClusterChartType = Extract<WidgetChartType, (typeof ClusterChartType)[number]>;

export type ChartType =
  | MetricChartType
  | CurrentChartType
  | TableChartType
  | ScatterChartType
  | TopologyChartType
  | HeatmapChartType
  | ClusterChartType;

export interface TargetTriggerInfo {
  targetId: string;
  targetName: string;
  triggerPath: WidgetTriggerPathType | null;
  targetAlias: string;
}

export const MetricDataField = ['clickedTime'] as const;
export type MetricDataField = (typeof MetricDataField)[number];

export const TimeseriesDragDataField = ['fromTime', 'toTime', ...MetricDataField] as const;
export type TimeseriesDragDataField = (typeof TimeseriesDragDataField)[number];

export type MetricDataFieldInfo = Record<TimeseriesDragDataField, string>;

export const CurrentDataField = ['targetId'] as const;
export type CurrentDataField = (typeof CurrentDataField)[number];

export type CurrentDataFieldInfo = Record<CurrentDataField, string>;

export const ScatterDataField = ['fromTime', 'toTime', 'minElapseTime', 'maxElapseTime'] as const;
export type ScatterDataField = (typeof ScatterDataField)[number];

export type ScatterDataFieldInfo = Record<ScatterDataField, string | number>;

export const HeatmapDataField = ['fromTime', 'toTime', 'minElapseTime', 'maxElapseTime'] as const;
export type HeatmapDataField = (typeof HeatmapDataField)[number];
export type HeatmapDataFieldInfo = Record<HeatmapDataField, string | number>;

export type TableDataFieldInfo = Record<string, RowInfo>;

export type TreeGridWidgetData = TreeGridRows<TableDataFieldInfo>;

export type WidgetChartDataWithIndex = (WidgetChartData | ClusterWidgetChartData) & {
  chartDataIndex: number;
};

export type DbClickParams = {
  targetId?: string;
  widgetStartTime?: number;
  widgetEndTime?: number;
};

export interface WidgetAlertInfo {
  showAlertIcon: boolean;
  isAlertHighlight: boolean;
  alertColor?: string;
  alertName?: string;
  fieldName?: string;
}
